import React, { useEffect, useMemo, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { Button, Modal, Row, Col, Form } from "react-bootstrap";
import {
  MdAddCircle,
  MdDelete,
  MdEdit,
  MdExpandMore,
  MdChevronRight,
  MdRefresh,
  MdUnfoldLess,
  MdUnfoldMore,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import loading from "../../../assets/images/common/loading.gif";
// ✅ add your CSS in your page css (coaRow, coaHeader... etc)

const ChartOfAccountsTree = () => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [tree, setTree] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // ✅ Selected node (optional - for future use)
  const [selectedNode, setSelectedNode] = useState(null);

  // Add Modal (kept as-is, but you asked Add should NAVIGATE, so modal is not used by Add button now)
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({
    node_name: "",
    parent_id: null,
    node_level: 1,
    sort_order: 0,
    node_code: "",
    is_active: true,
  });

  const resetAddForm = () =>
    setAddForm({
      node_name: "",
      parent_id: null,
      node_level: 1,
      sort_order: 0,
      node_code: "",
      is_active: true,
    });

  const closeAdd = () => {
    setShowAddModal(false);
    resetAddForm();
  };

  const levelLabel = (lvl) =>
    lvl === 1 ? "Parent Group" : lvl === 2 ? "Group" : "Account Head";

  const findNodeById = (nodes, id) => {
    for (const n of nodes || []) {
      if (n.node_id === id) return n;
      const f = findNodeById(n.children || [], id);
      if (f) return f;
    }
    return null;
  };

  // ✅ get full path (ancestors + node)
  const findPathById = (nodes, targetId, path = []) => {
    for (const n of nodes || []) {
      const newPath = [...path, n];
      if (n.node_id === targetId) return newPath;
      const kids = n.children || [];
      if (kids.length) {
        const got = findPathById(kids, targetId, newPath);
        if (got) return got;
      }
    }
    return null;
  };

  const collectAllIds = (nodes, acc = new Set()) => {
    (nodes || []).forEach((n) => {
      acc.add(n.node_id);
      if (n.children?.length) collectAllIds(n.children, acc);
    });
    return acc;
  };

  // ✅ Fetch COA Tree
  const fetchTree = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(`${baseUrl}/coa/tree`, {
        params: { _school_id: userObj.school_id },
      });

      const data = Array.isArray(res.data) ? res.data : [];
      setTree(data);

      // expand only level1 roots by default
      const next = new Set();
      data.forEach((n) => next.add(n.node_id));
      setExpanded(next);

      // keep selection if node still exists
      setSelectedNode((prev) => {
        if (!prev) return null;
        return findNodeById(data, prev.node_id) ? prev : null;
      });
    } catch (e) {
      console.error(e);
      setTree([]);
      toast.error("Failed to fetch COA tree");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ Expand/Collapse
  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => setExpanded(collectAllIds(tree, new Set()));
  const collapseAll = () => {
    const next = new Set();
    (tree || []).forEach((n) => next.add(n.node_id)); // keep root expanded
    setExpanded(next);
  };

  // ✅ Tree search (keeps parents if any child matches)
  const normalized = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
  const q = normalized(searchQuery);

  const filterTree = (nodes) => {
    if (!q) return nodes;

    const matchNode = (node) =>
      normalized(node.node_name).includes(q) || normalized(node.node_code).includes(q);

    const walk = (arr) =>
      (arr || [])
        .map((n) => {
          const kids = walk(n.children || []);
          const matched = matchNode(n) || kids.length > 0;
          if (!matched) return null;
          return { ...n, children: kids };
        })
        .filter(Boolean);

    return walk(nodes);
  };

  const filteredTree = useMemo(() => filterTree(tree), [tree, searchQuery]);

  // ✅ NAVIGATE ADD (as you asked)
  const handleAddNavigate = () => {
    navigate("/finance/chartofaccounts/add");
  };

  // ✅ EDIT: pass full path always
  const handleEdit = (node) => {
    const path = findPathById(tree, node.node_id) || [node];

    navigate("/finance/chartofaccounts/add", {
      state: {
        editNode: node,
        path,
      },
    });
  };

  const handleDelete = async (node) => {
    const ok = window.confirm(`Are you sure you want to delete "${node.node_name}"?`);
    if (!ok) return;

    try {
      await axios.post(`${baseUrl}/coa/node/procedure`, {
        action: "DELETE",
        school_id: userObj.school_id,
        node_id: node.node_id,
        lastmodifiedby: userObj.user_name,
      });

      toast.success("Deleted successfully");
      if (selectedNode?.node_id === node.node_id) setSelectedNode(null);
      fetchTree();
    } catch (e) {
      console.error(e);
      toast.error("Delete failed");
    }
  };

  // ✅ Render one node row
  const NodeRow = ({ node, depth = 0 }) => {
    const hasChildren = (node.children || []).length > 0;
    const isOpen = expanded.has(node.node_id);
    const isSelected = selectedNode?.node_id === node.node_id;

    return (
      <div className="coaRowWrap" style={{ paddingLeft: depth * 18 }}>
        <div
          className={`coaRow ${isSelected ? "coaRowSelected" : ""}`}
          onClick={() => setSelectedNode(node)}
        >
          <div
            className="coaExpander"
            title={hasChildren ? (isOpen ? "Collapse" : "Expand") : ""}
            onClick={(ev) => {
              ev.stopPropagation();
              if (hasChildren) toggleExpand(node.node_id);
            }}
            style={{ opacity: hasChildren ? 1 : 0.25 }}
          >
            {hasChildren ? (
              isOpen ? (
                <MdExpandMore size={20} />
              ) : (
                <MdChevronRight size={20} />
              )
            ) : (
              <MdChevronRight size={20} />
            )}
          </div>

          {depth > 0 ? <div className="coaIndent" /> : null}

          <div className="coaMain">
            <div className="coaNameLine">
              <div className="coaName">{node.node_name}</div>

              <span className="coaBadge">{levelLabel(node.node_level)}</span>

              {node.node_code ? (
                <span className="coaBadge coaBadgeCode">{node.node_code}</span>
              ) : null}

              {node.is_active ? (
                <span className="coaBadge coaBadgeActive">Active</span>
              ) : (
                <span className="coaBadge coaBadgeInactive">Inactive</span>
              )}
            </div>

            <div className="coaMeta">
              {node.parent_id ? `Parent: ${node.parent_id}` : "Root"} • ID: {node.node_id}
            </div>
          </div>

          {/* ✅ Actions for ALL LEVELS */}
          <div className="coaRowActions" onClick={(e) => e.stopPropagation()}>
            <div className="coaIconBtn" title="Edit" onClick={() => handleEdit(node)}>
              <MdEdit />
            </div>
            <div className="coaIconBtn" title="Delete" onClick={() => handleDelete(node)}>
              <MdDelete />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTree = (nodes, depth = 0) => {
    return (nodes || []).map((n) => {
      const hasChildren = (n.children || []).length > 0;
      const isOpen = expanded.has(n.node_id);

      return (
        <React.Fragment key={n.node_id}>
          <NodeRow node={n} depth={depth} />
          {hasChildren && isOpen ? renderTree(n.children, depth + 1) : null}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="pageBody coaWrap">
      <ToastContainer />

      <div className="coaCard">
        <div className="coaHeader">
          <div className="coaTitleBlock">
            <p className="coaTitle">Chart of Accounts (Tree)</p>
            <div className="coaSub">
              {selectedNode ? (
                <>
                  Selected: <b>{selectedNode.node_name}</b> ({levelLabel(selectedNode.node_level)})
                </>
              ) : (
                "Select a node"
              )}
            </div>
          </div>

          <div className="coaSearch">
            <input
              type="text"
              placeholder="Search node name / code..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="coaActions">
            <Button className="secondaryBtn" variant="secondary" onClick={fetchTree} title="Refresh">
              <MdRefresh />
            </Button>

            <Button className="secondaryBtn" variant="secondary" onClick={expandAll} title="Expand All">
              <MdUnfoldMore />
            </Button>

            <Button className="secondaryBtn" variant="secondary" onClick={collapseAll} title="Collapse All">
              <MdUnfoldLess />
            </Button>

            {/* ✅ ADD NAVIGATE */}
            <Button className="primaryBtn" variant="primary" onClick={handleAddNavigate} title="Add">
              <MdAddCircle /> <span style={{ marginLeft: 6 }}>Add</span>
            </Button>
          </div>
        </div>

        <div className="coaTreeBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : filteredTree.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", opacity: 0.7 }}>No nodes found</div>
          ) : (
            <div>{renderTree(filteredTree)}</div>
          )}
        </div>
      </div>

      {/* (Optional) Old Modal kept - not used by Add button now */}
      <Modal show={showAddModal} onHide={closeAdd} className="commonFilterModal" backdrop="static" centered>
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Add Node</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Node Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={addForm.node_name}
                    onChange={(e) => setAddForm({ ...addForm, node_name: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="d-flex justify-content-end mt-3" style={{ gap: 10 }}>
              <Button type="button" variant="secondary" className="secondaryBtn" onClick={closeAdd}>
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ChartOfAccountsTree;